import React, { useMemo, useState, useCallback } from "react";
import { Box, CircularProgress, Snackbar, Alert, Typography, Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions, Button } from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { Model, CustomError } from "survey-core";
import { Survey } from "survey-react-ui";
import "survey-core/survey-core.min.css";
import { useNavigate } from "react-router-dom";
import { ROUTE_SURVEY_HOME } from "constants/routerPath";
import { useSurvey, useToast } from "hooks/survey";

function PreviewSurvey() {
  const { surveyJson, loading, error } = useSurvey();
  const { toast, closeToast } = useToast();
  const [previewDialogOpen, setPreviewDialogOpen] = useState(false);
  const navigate = useNavigate();

  const handleCompleteAttempt = useCallback(() => {
    setPreviewDialogOpen(true);
  }, []);

  // Memoize survey model to prevent recreation on every render
  const survey = useMemo(() => {
    if (!surveyJson) {
      return null;
    }
    const model = new Model(surveyJson);

    model.applyTheme({
      cssVariables: {
        "--sjs-general-backcolor-dim": "transparent",
      },
    });

    // Handle page navigation with answer grading
    model.onCurrentPageChanging.add((sender, options) => {
      // Only validate when moving forward
      if (!options.isNextPage) return;
      const currentPage = sender.currentPage;
      const questions = currentPage.questions;
      let hasIncorrect = false;
      for (const question of questions) {
        // Clear previous errors
        question.clearErrors();
        // Skip if not gradeable
        const hasCorrectAnswer = question.correctAnswer !== undefined
            && question.correctAnswer !== null;
        if (!question.isRequired || !hasCorrectAnswer) {
          continue;
        }
        try {
          if (!question.isAnswerCorrect()) {
            question.addError(new CustomError("Incorrect answer. Please try again."));
            hasIncorrect = true;
          }
        } catch (e) {
          // Skip grading for unrecognized question types or errors
          console.warn(`Grading skipped for question "${question.name}":`, e);
        }
      }
      if (hasIncorrect) {
        options.allow = false;
      }
    });

    // Block survey completion and show preview dialog
    model.onCompleting.add((sender, options) => {
      options.allow = false;
      handleCompleteAttempt();
    });

    return model;
  }, [surveyJson, handleCompleteAttempt]);

  // Show loading spinner while fetching survey
  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="100vh">
        <CircularProgress />
      </Box>
    );
  }

  // Show error state
  if (error || !surveyJson || !survey) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="100vh">
        <Typography variant="h6" color="error">
          {error || "Failed to load the survey. Please try again later."}
        </Typography>
      </Box>
    );
  }

  return (
    <>
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mx: 2, mt: 2 }}>
        <Button
          variant="outlined"
          color="primary"
          size="small"
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate(ROUTE_SURVEY_HOME)}
        >
          Back to Surveys
        </Button>
        <Typography variant="body2" color="text.secondary">
          Preview mode — responses are not being recorded.
        </Typography>
      </Box>

      <Survey model={survey} />

      <Dialog open={previewDialogOpen} onClose={() => setPreviewDialogOpen(false)}>
        <DialogTitle>Preview Mode</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This is a preview of the survey. Responses are not being recorded.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPreviewDialogOpen(false)}>Continue Previewing</Button>
          <Button variant="contained" onClick={() => navigate(ROUTE_SURVEY_HOME)}>
            Return to Surveys
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for toast notifications */}
      <Snackbar
        open={toast.open}
        autoHideDuration={3000}
        onClose={closeToast}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert onClose={closeToast} severity={toast.severity}>
          {toast.message}
        </Alert>
      </Snackbar>
    </>
  );
}

export default PreviewSurvey;
